"use client";

import { Card } from "@/components/ui/card";
import {} from "@/components/ui/chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {} from "@/components/ui/tooltip";
import { cn, formatCurrency } from "@/lib/utils";
import { format, parseISO, subMonths } from "date-fns";
import {
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  Home,
  Lightbulb,
  PercentCircle,
  Timer,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

interface AggregateData {
  date: string;
  value: string;
  yoy: string;
}

interface MetricData {
  label: string;
  value: string;
  aggregateData: AggregateData[];
}

interface HistoricalMarketPulseProps {
  homePrices: MetricData[];
  demand: MetricData[];
}

// Helper to parse price strings into numbers
function parsePriceString(price: string): number {
  return Number(price.replace(/[^0-9.-]/g, ""));
}

// Helper to parse percentage strings into numbers
function parsePercentageString(percentage: string): number {
  // Handle empty strings
  if (!percentage) return 0;
  // Remove % symbol and convert to decimal
  return Number(percentage.replace(/[^0-9.-]/g, "")) / 100;
}

// Format date strings consistently
function formatDateString(dateString: string): string {
  try {
    const date = parseISO(dateString);
    return format(date, "MMM yyyy");
  } catch (e) {
    return dateString;
  }
}

function calculatePercentChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

// Get most significant trend for AI insight
function getMostSignificantTrend(data: ChartDataPoint[]): {
  metric: string;
  change: number;
  period: string;
  favorsBuyers: boolean;
} {
  if (!data.length || data.length < 3) {
    return {
      metric: "prices",
      change: 0,
      period: "recent months",
      favorsBuyers: false,
    };
  }

  // Calculate changes over different periods for each metric
  const latestDate = new Date(data[data.length - 1].date);
  const threeMonthsAgo = subMonths(latestDate, 3);

  // Calculate changes for each metric
  const priceChange = calculatePercentChange(
    data[data.length - 1].price,
    data.find((point) => new Date(point.date) <= threeMonthsAgo)?.price ||
      data[0].price,
  );

  const domsChange = calculatePercentChange(
    data[data.length - 1].daysOnMarket,
    data.find((point) => new Date(point.date) <= threeMonthsAgo)
      ?.daysOnMarket || data[0].daysOnMarket,
  );

  const supplyChange = calculatePercentChange(
    data[data.length - 1].homesSold,
    data.find((point) => new Date(point.date) <= threeMonthsAgo)?.homesSold ||
      data[0].homesSold,
  );

  const ratioChange = calculatePercentChange(
    data[data.length - 1].saleToListRatio,
    data.find((point) => new Date(point.date) <= threeMonthsAgo)
      ?.saleToListRatio || data[0].saleToListRatio,
  );

  // Determine most significant change by magnitude, factoring in buyer favorability
  const changes = [
    {
      metric: "days on market",
      change: domsChange,
      favorsBuyers: domsChange > 0,
    },
    {
      metric: "home prices",
      change: priceChange,
      favorsBuyers: priceChange < 0,
    },
    {
      metric: "home sales",
      change: supplyChange,
      favorsBuyers: supplyChange < 0,
    },
    {
      metric: "sale-to-list ratio",
      change: ratioChange,
      favorsBuyers: ratioChange < 0,
    },
  ];

  // Sort by absolute magnitude of change
  changes.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

  return {
    metric: changes[0].metric,
    change: changes[0].change,
    period: "3 months",
    favorsBuyers: changes[0].favorsBuyers,
  };
}

interface ChartDataPoint {
  date: string;
  price: number;
  homesSold: number;
  daysOnMarket: number;
  saleToListRatio: number;
}

type MetricKey = "price" | "homesSold" | "daysOnMarket" | "saleToListRatio";

interface MetricConfig {
  key: MetricKey;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  textColor: string;
  icon: React.ReactNode;
  formatter: (value: number) => string;
  isBuyerFavorable: boolean;
}

export function HistoricalMarketPulse({
  homePrices,
  demand,
}: HistoricalMarketPulseProps) {
  const [activeView, setActiveView] = useState<"6m" | "12m">("6m");
  const [activeMetric, setActiveMetric] = useState<MetricKey>("price");
  const [hoveredPoint, setHoveredPoint] = useState<ChartDataPoint | null>(null);

  // Find the required metrics
  const medianSalePrice = homePrices.find(
    (price) => price.label === "Median Sale Price",
  );
  const medianDaysOnMarket = homePrices.find(
    (price) => price.label === "Median Days on Market",
  );
  const homesSold = homePrices.find(
    (price) => price.label === "# of Homes Sold",
  );
  const saleToListRatio = demand.find(
    (item) => item.label === "Sale-to-List Price",
  );

  // Metric configurations
  const metricConfigs: MetricConfig[] = [
    {
      key: "price",
      label: "Median Sale Price",
      description: "The middle price point of all homes sold in the area",
      color: "#818CF8",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-900",
      icon: <DollarSign className="h-4 w-4" />,
      formatter: (value) => formatCurrency(value),
      isBuyerFavorable: false,
    },
    {
      key: "homesSold",
      label: "Homes Sold",
      description:
        "Number of homes sold in the last month, indicating market activity",
      color: "#34D399",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-900",
      icon: <Home className="h-4 w-4" />,
      formatter: (value) => value.toString(),
      isBuyerFavorable: false,
    },
    {
      key: "daysOnMarket",
      label: "Days on Market",
      description: "Average time homes spend listed before selling",
      color: "#F97316",
      bgColor: "bg-orange-50",
      textColor: "text-orange-900",
      icon: <Timer className="h-4 w-4" />,
      formatter: (value) => `${value} days`,
      isBuyerFavorable: true,
    },
    {
      key: "saleToListRatio",
      label: "% of Asking Price",
      description:
        "How close homes sell to their list price - 100% means selling at asking price",
      color: "#EC4899",
      bgColor: "bg-pink-50",
      textColor: "text-pink-900",
      icon: <PercentCircle className="h-4 w-4" />,
      formatter: (value) => `${(value * 100).toFixed(1)}%`,
      isBuyerFavorable: false,
    },
  ];

  // Get current active metric config
  const activeMetricConfig = metricConfigs.find(
    (config) => config.key === activeMetric,
  )!;

  // Process and merge the data for the chart
  const chartData = useMemo(() => {
    if (
      !medianSalePrice?.aggregateData ||
      !medianDaysOnMarket?.aggregateData ||
      !homesSold?.aggregateData ||
      !saleToListRatio?.aggregateData
    ) {
      return [];
    }

    // Create data points from all available dates
    const allDates = new Set<string>();
    [
      ...medianSalePrice.aggregateData,
      ...medianDaysOnMarket.aggregateData,
      ...homesSold.aggregateData,
      ...saleToListRatio.aggregateData,
    ].forEach((item) => {
      allDates.add(item.date);
    });

    // Convert to array and sort by date
    const sortedDates = Array.from(allDates).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );

    return sortedDates.map((date) => {
      const priceData = medianSalePrice.aggregateData.find(
        (d) => d.date === date,
      );
      const daysData = medianDaysOnMarket.aggregateData.find(
        (d) => d.date === date,
      );
      const soldData = homesSold.aggregateData.find((d) => d.date === date);
      const ratioData = saleToListRatio.aggregateData.find(
        (d) => d.date === date,
      );

      return {
        date,
        formattedDate: formatDateString(date),
        price: priceData ? parsePriceString(priceData.value) : 0,
        daysOnMarket: daysData ? parsePriceString(daysData.value) : 0,
        homesSold: soldData ? parsePriceString(soldData.value) : 0,
        saleToListRatio: ratioData ? Number(ratioData.value) : 0,
      };
    });
  }, [medianSalePrice, medianDaysOnMarket, homesSold, saleToListRatio]);

  // Filter data based on selected time period
  const filteredData = useMemo(() => {
    if (!chartData.length) return [];

    const now = new Date(chartData[chartData.length - 1].date);
    const cutoffDate =
      activeView === "6m" ? subMonths(now, 6) : subMonths(now, 12);

    return chartData.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= cutoffDate;
    });
  }, [chartData, activeView]);

  // Get market insight
  const marketInsight = useMemo(() => {
    return getMostSignificantTrend(filteredData);
  }, [filteredData]);

  // Determine colors and direction indicators for each metric
  const getMetricInfo = (
    current: number,
    previous: number,
    isBuyerFavorable: boolean,
  ): { change: number; direction: "up" | "down"; color: string } => {
    const change = calculatePercentChange(current, previous);
    const direction = change >= 0 ? "up" : "down";
    let color = "";

    if (isBuyerFavorable) {
      // For metrics where increase favors buyers (days on market)
      color = direction === "up" ? "text-emerald-600" : "text-orange-600";
    } else {
      // For metrics where decrease favors buyers (price, ratio)
      color = direction === "up" ? "text-orange-600" : "text-emerald-600";
    }

    return { change, direction, color };
  };

  if (!chartData.length) {
    return (
      <Card className="p-6">
        <div className="text-muted-foreground text-center">
          Historical market data is not available
        </div>
      </Card>
    );
  }

  // Get latest and previous values for comparisons
  const latest = chartData[chartData.length - 1];
  const threeMonthsAgo = subMonths(new Date(latest.date), 3);
  const previousData =
    chartData.find((d) => new Date(d.date) <= threeMonthsAgo) || chartData[0];

  // Calculate changes for each metric
  const metricStats = metricConfigs.map((config) => {
    const current = latest[config.key];
    const previous = previousData[config.key];
    const info = getMetricInfo(current, previous, config.isBuyerFavorable);

    return {
      ...config,
      value: current,
      previousValue: previous,
      change: info.change,
      direction: info.direction,
      colorClass: info.color,
    };
  });

  return (
    <Card className="overflow-hidden">
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Historical Market Pulse</h3>
            <p className="text-sm text-muted-foreground">
              Key metrics over time, revealing market momentum and patterns
            </p>
          </div>
          <Tabs
            value={activeView}
            onValueChange={(v) => setActiveView(v as "6m" | "12m")}
            className="h-9"
          >
            <TabsList className="bg-muted/50">
              <TabsTrigger value="6m" className="text-xs">
                6 Months
              </TabsTrigger>
              <TabsTrigger value="12m" className="text-xs">
                12 Months
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Insight Callout */}
        <div className="rounded-lg bg-blue-50 p-4 border border-blue-100">
          <div className="flex gap-3">
            <div className="mt-0.5">
              <Lightbulb className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">
                Negotiation Insight
              </h4>
              <p className="text-sm text-blue-700">
                {marketInsight.metric === "sale-to-list ratio"
                  ? "% of asking price"
                  : marketInsight.metric.charAt(0).toUpperCase() +
                    marketInsight.metric.slice(1)}{" "}
                {marketInsight.change >= 0 ? "increasing" : "decreasing"}{" "}
                {Math.abs(marketInsight.change).toFixed(1)}% over the past{" "}
                {marketInsight.period} suggests{" "}
                {marketInsight.favorsBuyers
                  ? "growing buyer leverage"
                  : "stronger seller position"}
                .
              </p>
            </div>
          </div>
        </div>

        {/* Metric Selection Tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {metricStats.map((metric) => (
            <button
              key={metric.key}
              type="button"
              onClick={() => setActiveMetric(metric.key)}
              className={cn(
                "p-3 rounded-lg transition-all text-left relative group",
                activeMetric === metric.key
                  ? `${metric.bgColor} ring-2 ring-offset-2`
                  : "bg-gray-50 hover:bg-gray-100",
                activeMetric === metric.key
                  ? `ring-${metric.color.replace("#", "")}`
                  : "",
              )}
            >
              <div
                className={cn(
                  "flex items-center gap-2",
                  activeMetric === metric.key
                    ? metric.textColor
                    : "text-gray-700",
                )}
              >
                {metric.icon}
                <span className="text-xs font-semibold">{metric.label}</span>
              </div>
              <div className="mt-1.5">
                <div className="text-lg font-semibold">
                  {metric.formatter(metric.value)}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span
                    className={cn("text-xs font-medium", metric.colorClass)}
                  >
                    {metric.direction === "up" ? (
                      <ArrowUpRight className="inline h-3 w-3" />
                    ) : (
                      <ArrowDownRight className="inline h-3 w-3" />
                    )}
                    {Math.abs(metric.change).toFixed(1)}%
                  </span>
                  <span className="text-xs text-gray-500">in 3m</span>
                </div>
              </div>
              {activeMetric === metric.key && (
                <div className="mt-2 pt-2 border-t text-xs text-gray-600">
                  {metric.description}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredData}
              margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
              onMouseMove={(e) => {
                if (e.activePayload?.[0]?.payload) {
                  setHoveredPoint(e.activePayload[0].payload as ChartDataPoint);
                }
              }}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              <defs>
                <linearGradient
                  id={`color${activeMetric}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={activeMetricConfig.color}
                    stopOpacity={0.2}
                  />
                  <stop
                    offset="95%"
                    stopColor={activeMetricConfig.color}
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e5e7eb"
              />

              <XAxis
                dataKey="formattedDate"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => value}
              />

              <YAxis
                domain={["auto", "auto"]}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => {
                  if (activeMetric === "price")
                    return `$${(value / 1000).toFixed(0)}k`;
                  if (activeMetric === "saleToListRatio")
                    return `${(value * 100).toFixed(0)}%`;
                  return value.toString();
                }}
              />

              <RechartsTooltip
                cursor={{ stroke: "#9CA3AF", strokeDasharray: "3 3" }}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;

                  const data = payload[0].payload as ChartDataPoint;
                  const metric = activeMetricConfig;

                  return (
                    <div className="bg-white p-3 shadow-md rounded-lg border border-gray-100">
                      <div className="font-medium text-sm mb-2">
                        {formatDateString(data.date)}
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-1.5">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: metric.color }}
                            />
                            <span className="text-xs text-gray-600">
                              {metric.label}:
                            </span>
                          </div>
                          <span className="text-xs font-medium">
                            {metric.formatter(data[metric.key])}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />

              <Line
                type="monotone"
                dataKey={activeMetric}
                stroke={activeMetricConfig.color}
                strokeWidth={3}
                fill={`url(#color${activeMetric})`}
                activeDot={{
                  r: 6,
                  fill: activeMetricConfig.color,
                  stroke: "white",
                  strokeWidth: 2,
                }}
                dot={{
                  r: 4,
                  fill: "white",
                  stroke: activeMetricConfig.color,
                  strokeWidth: 1.5,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Historical Comparison Section */}
        <div className="p-4 border rounded-lg bg-gray-50">
          <div className="text-sm font-semibold mb-2">
            Historical Comparison
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Current Value</div>
              <div className="text-base font-semibold">
                {activeMetricConfig.formatter(
                  hoveredPoint
                    ? hoveredPoint[activeMetricConfig.key]
                    : latest[activeMetricConfig.key],
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">3 Months Ago</div>
              <div className="flex items-center">
                <div className="text-base font-semibold">
                  {activeMetricConfig.formatter(
                    previousData[activeMetricConfig.key],
                  )}
                </div>
                <div
                  className={cn(
                    "ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full",
                    metricStats.find((m) => m.key === activeMetric)
                      ?.colorClass === "text-emerald-600"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-orange-100 text-orange-700",
                  )}
                >
                  {metricStats.find((m) => m.key === activeMetric)
                    ?.direction === "up"
                    ? "+"
                    : ""}
                  {Math.abs(
                    metricStats.find((m) => m.key === activeMetric)?.change ||
                      0,
                  ).toFixed(1)}
                  %
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
